import React, {
  Suspense,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import { AuthContext } from "src/utils/AuthContext.js";
import { customEvent } from "src/utils/gtag.js";
import { lazyWithRetry } from "src/utils/lazyWithRetry.js";
import { buildReviewEventData } from "src/utils/useSubmitReview.js";
import {
  calendarReviewCheckedKey,
  calendarReviewNotCheckedRecently,
  calendarReviewNotShownRecently,
  calendarReviewShownKey,
  INTERSTITIAL_KEYS,
  isSuppressedRoute,
  outrankedInterstitial,
  stampNow,
} from "src/utils/interstitial.js";
import { useReviewPromptCalendar } from "src/services/queries/useReviewPromptCalendar.js";


// Deferred so the review form stays out of the entry bundle for the visitors
// (anonymous, or with nothing left to review) who never see it.
const CalendarReviewInterstitialDialog = lazyWithRetry(() =>
  import("src/components/blocks/reviews/CalendarReviewInterstitialDialog.js")
);

/**
 * Interstitial nudging signed-in users to review a calendar they subscribe to
 * but have not rated, to grow reviews and the discovery network effect.
 */
export default function CalendarReviewInterstitial() {
  // Mounted at app root, so tolerate rendering outside AuthProvider.
  const { user, isLoading } = useContext(AuthContext) ?? {};
  const location = useLocation();
  const [open, setOpen] = useState(false);
  // Set once the dialog actually mounts; guards reopening after dismissal and
  // survives a transient open interrupted by post-sign-in navigation.
  const shownRef = useRef(false);
  const openedAtLocationRef = useRef(null);
  // True once the user rated, so closing afterwards reports a conversion rather
  // than a dismissal.
  const ratedRef = useRef(false);
  // Latched for the load once the upgrade interstitial is due, so the review
  // nudge yields the slot this session and does not stack on top of it even
  // after the upgrade stamps its fatigue key. Evaluated inline as well, because
  // the effect that latches it only runs after the first render commits.
  const deferredToLimitRef = useRef(false);

  const onSuppressedRoute = isSuppressedRoute(location);
  const locationKey = `${location.pathname}${location.search}${location.hash}`;
  const limitWillShow = outrankedInterstitial(INTERSTITIAL_KEYS.CALENDAR_REVIEW, user);
  const deferredToLimit = limitWillShow || deferredToLimitRef.current;

  useEffect(() => {
    if (limitWillShow) deferredToLimitRef.current = true;
  }, [limitWillShow]);

  // Only hit the endpoint when the interstitial is otherwise eligible, so it
  // costs ~one request per fatigue window rather than one per signed-in load.
  const canCheck =
    !!user &&
    !isLoading &&
    !deferredToLimit &&
    !onSuppressedRoute &&
    !shownRef.current &&
    calendarReviewNotShownRecently(user?._id) &&
    calendarReviewNotCheckedRecently(user?._id);
  const { data: promptCalendar, isSuccess } = useReviewPromptCalendar({
    userId: user?._id,
    enabled: canCheck,
  });

  // A null answer means there is nothing to review right now. Stamp it so the
  // endpoint is not re-queried on every page load for the (growing) population
  // that has rated everything or synced nothing.
  useEffect(() => {
    if (!user?._id || !isSuccess || promptCalendar) return;
    stampNow(calendarReviewCheckedKey(user?._id));
  }, [isSuccess, promptCalendar, user?._id]);

  useEffect(() => {
    if (open || !canCheck || !promptCalendar) return;
    openedAtLocationRef.current = locationKey;
    setOpen(true);
  }, [open, canCheck, promptCalendar, locationKey]);

  // Fired by the dialog on mount, so a chunk that never loads (or an open
  // interrupted before it renders) neither reports an impression nor spends the
  // fatigue window nor burns the slot.
  const handleShown = useCallback(() => {
    shownRef.current = true;
    stampNow(calendarReviewShownKey(user?._id));
    customEvent({
      name: "calendar_review_interstitial_shown",
      category: "Reviews",
      label: "Calendar Review Interstitial Shown",
      data: buildReviewEventData(user, promptCalendar),
    });
  }, [user, promptCalendar]);

  // Dismiss on navigation so the modal never sits on top of the destination.
  useEffect(() => {
    if (!openedAtLocationRef.current) return;
    if (openedAtLocationRef.current === locationKey) return;
    setOpen(false);
  }, [locationKey]);

  // Sign-out ends the session: reset the per-load guard so the next sign-in
  // re-evaluates. Cross-session re-shows stay capped by the fatigue window.
  useEffect(() => {
    if (user) return;
    shownRef.current = false;
    ratedRef.current = false;
    openedAtLocationRef.current = null;
    deferredToLimitRef.current = false;
    setOpen(false);
  }, [user]);

  if (!open || !user || !promptCalendar) return null;

  const eventData = buildReviewEventData(user, promptCalendar);

  // A saved star rating is already a published review, so report the conversion
  // there rather than only on the optional title/comment step.
  const handleRated = () => {
    if (ratedRef.current) return;
    ratedRef.current = true;
    customEvent({
      name: "calendar_review_interstitial_submitted",
      category: "Reviews",
      label: "Calendar Review Interstitial Submitted",
      data: eventData,
    });
  };
  const handleClose = () => {
    setOpen(false);
    if (ratedRef.current) return;
    customEvent({
      name: "calendar_review_interstitial_dismissed",
      category: "Reviews",
      label: "Calendar Review Interstitial Dismissed",
      data: eventData,
    });
  };

  return (
    <Suspense fallback={null}>
      <CalendarReviewInterstitialDialog
        calendar={promptCalendar}
        onShown={handleShown}
        onRated={handleRated}
        onClose={handleClose}
      />
    </Suspense>
  );
}
